import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { useFormikContext } from "formik";
import React, { useState } from "react";
import { Col, Form, Row } from "react-bootstrap";

import { NRIStatusForm } from "..";
import {
  CountryWithCode,
  ResidentialStatus,
  StateWithCode
} from "../../../../config/filingConstants";
import { IFormITR1 } from "../../../../config/interfaces";
import { UtilService } from "../../../../services";
import FASvgIcon from "../../../FASvgIcon";
import PreventEditComponent from "../../../PreventEditComponent";
import { FormField, NumericInputField, SelectInputField, TextInputField } from "../../../fields";
import ResidentialStatusForm from "../sidebar-forms/ResidentialStatusForm";

type Props = {};

const AddressInfo: React.FC<Props> = (props: Props) => {
  const [showStatusFormCanvas, setShowStatusFormCanvas] = useState<boolean>(false);

  const { values, errors, touched, setFieldValue, handleBlur, setFieldTouched, setTouched } =
    useFormikContext<IFormITR1>();

  return (
    <>
      <hr />
      <section>
        <p className="leading-para fw-bold text-primary">Address</p>

        <FormField label="Flat/Door No." isRequired={true}>
          <TextInputField
            name="Address.ResidenceNo"
            value={values.Address.ResidenceNo}
            onBlur={(e) => {
              handleBlur(e);
              setFieldValue("Address.ResidenceNo", values.Address.ResidenceNo.trim());
            }}
            onChange={(value) => setFieldValue("Address.ResidenceNo", value)}
            isInvalid={Boolean(errors.Address?.ResidenceNo) && touched.Address?.ResidenceNo}
            errorMsg={errors.Address?.ResidenceNo}
          />
        </FormField>

        <FormField label="Premises Name" isRequired={true}>
          <TextInputField
            name="Address.ResidenceName"
            value={values.Address.ResidenceName}
            onBlur={(e) => {
              handleBlur(e);
              setFieldValue("Address.ResidenceName", values.Address.ResidenceName.trim());
            }}
            onChange={(value) => setFieldValue("Address.ResidenceName", value)}
            isInvalid={Boolean(errors.Address?.ResidenceName) && touched.Address?.ResidenceName}
            errorMsg={errors.Address?.ResidenceName}
          />
        </FormField>

        <FormField label="Road/Street" isRequired={true}>
          <TextInputField
            name="Address.RoadOrStreet"
            value={values.Address.RoadOrStreet}
            onBlur={(e) => {
              handleBlur(e);
              setFieldValue("Address.RoadOrStreet", values.Address.RoadOrStreet.trim());
            }}
            onChange={(value) => setFieldValue("Address.RoadOrStreet", value)}
            isInvalid={Boolean(errors.Address?.RoadOrStreet) && touched.Address?.RoadOrStreet}
            errorMsg={errors.Address?.RoadOrStreet}
          />
        </FormField>

        <FormField label="Area/Locality" isRequired={true}>
          <TextInputField
            name="Address.LocalityOrArea"
            value={values.Address.LocalityOrArea}
            onBlur={(e) => {
              handleBlur(e);
              setFieldValue("Address.LocalityOrArea", values.Address.LocalityOrArea.trim());
            }}
            onChange={(value) => setFieldValue("Address.LocalityOrArea", value)}
            isInvalid={Boolean(errors.Address?.LocalityOrArea) && touched.Address?.LocalityOrArea}
            errorMsg={errors.Address?.LocalityOrArea}
          />
        </FormField>

        <FormField label="Pincode/Zipcode" isRequired={true}>
          <NumericInputField
            name="Address.PinCode"
            value={values.Address?.PinCode || ""}
            onBlur={handleBlur("Address.PinCode")}
            onChange={async (value) => {
              setFieldValue("Address.PinCode", Number(value));
              setFieldValue("Address.ZipCode", value);

              if (value.length === 6) {
                const data = await UtilService.getStateCityFromPincode(value);
                if (data) {
                  setFieldValue("Address.CountryCode", data.CountryCode);
                  setFieldValue("Address.StateCode", data.StateCode);
                  setFieldValue("Address.CityOrTownOrDistrict", data.CityOrTownOrDistrict);
                } else {
                  setFieldValue("Address.CountryCode", "");
                  setFieldValue("Address.StateCode", "");
                  setFieldValue("Address.CityOrTownOrDistrict", "");
                }
              }
            }}
            isInvalid={Boolean(errors.Address?.PinCode) && touched.Address?.PinCode}
            errorMsg={errors.Address?.PinCode}
            decimalUpto={0}
            separator=""
          />
        </FormField>

        <FormField label="Country | State | City" isRequired={true} sm={3}>
          <Row className="align-items-center">
            <Col sm={4}>
              <SelectInputField
                placeholder="Select Country"
                name="Address.CountryCode"
                menuZIndex={9}
                label="Country"
                isSearchable={true}
                isOptionArrayOfObject={true}
                options={UtilService.convertToArray(CountryWithCode)}
                value={{
                  "label": CountryWithCode[values.Address.CountryCode],
                  "value": values.Address.CountryCode
                }}
                onChange={(selectedCountry) => {
                  if (selectedCountry) {
                    setFieldValue("Address.CountryCode", selectedCountry.value);
                    setFieldValue("Address.CityOrTownOrDistrict", "");
                    if (selectedCountry.value !== "91") {
                      setFieldValue("Address.StateCode", "99");
                    } else {
                      setFieldValue("Address.StateCode", "");
                    }
                  } else {
                    setFieldValue("Address.CountryCode", undefined);
                  }
                }}
                onBlur={handleBlur("Address.CountryCode")}
                isInvalid={Boolean(errors.Address?.CountryCode) && touched.Address?.CountryCode}
                errorMsg={errors.Address?.CountryCode}
              />
            </Col>
            <Col sm={4}>
              <SelectInputField
                placeholder="Select state"
                name="Address.StateCode"
                menuZIndex={9}
                label="State"
                isSearchable={true}
                isOptionArrayOfObject={true}
                options={UtilService.getStateOptions(values.Address.CountryCode)}
                value={{
                  "label": StateWithCode[values.Address.StateCode],
                  "value": values.Address.StateCode
                }}
                onChange={(selectedState) => {
                  if (selectedState) {
                    setFieldValue("Address.StateCode", selectedState.value);
                  } else {
                    setFieldValue("Address.StateCode", undefined);
                  }
                }}
                onBlur={handleBlur("Address.StateCode")}
                isInvalid={Boolean(errors.Address?.StateCode) && touched.Address?.StateCode}
                errorMsg={errors.Address?.StateCode}
              />
            </Col>
            <Col sm={4}>
              <TextInputField
                name="Address.CityOrTownOrDistrict"
                label="City"
                value={values.Address.CityOrTownOrDistrict}
                onChange={(value) => {
                  setFieldValue("Address.CityOrTownOrDistrict", value);
                }}
                onBlur={handleBlur("Address.CityOrTownOrDistrict")}
                isInvalid={
                  Boolean(errors.Address?.CityOrTownOrDistrict) &&
                  touched.Address?.CityOrTownOrDistrict
                }
                errorMsg={errors.Address?.CityOrTownOrDistrict}
              />
            </Col>
          </Row>
        </FormField>

        <FormField label="Residential Status" isRequired={true}>
          <>
            <Row>
              <Col>
                <SelectInputField
                  placeholder="Select status"
                  name="Address.ResidentialStatus"
                  menuZIndex={9}
                  isSearchable={true}
                  isOptionArrayOfObject={false}
                  options={ResidentialStatus}
                  value={[values.Address.ResidentialStatus]}
                  onChange={(selectedOption) => {
                    if (selectedOption) {
                      setFieldValue("Address.ResidentialStatus", selectedOption.value);
                    } else {
                      setFieldValue("Address.ResidentialStatus", undefined);
                    }
                    setFieldValue("NRIJurisdictionDetails", [["", ""]]);
                    setFieldValue("NRIStayInIndia1", "");
                    setFieldValue("NRIStayInIndia2", "");
                  }}
                  onBlur={handleBlur("Address.ResidentialStatus")}
                  isInvalid={
                    Boolean(errors.Address?.ResidentialStatus) && touched.Address?.ResidentialStatus
                  }
                  errorMsg={errors.Address?.ResidentialStatus}
                />
              </Col>
            </Row>
            <PreventEditComponent>
              <Row className="mt-n4 mb-4">
                <Col sm={12}>
                  <Form.Text className="field-text text-greyDark m-0">
                    <FASvgIcon icon={faInfoCircle} /> Need more information? please&nbsp;
                    <span
                      role="button"
                      className="text-secondary fw-bold"
                      onClick={() => setShowStatusFormCanvas(true)}
                    >
                      click here
                    </span>
                  </Form.Text>
                </Col>
              </Row>
            </PreventEditComponent>
          </>
        </FormField>

        {values.Address.ResidentialStatus === "Non-Resident" && <NRIStatusForm />}
      </section>

      <ResidentialStatusForm
        showCanvas={showStatusFormCanvas}
        handleCloseBtnPress={(status) => {
          if (status) {
            setFieldValue("Address.ResidentialStatus", status);
          }
          setShowStatusFormCanvas(() => false);
        }}
        formValues={""}
      />
    </>
  );
};

export default AddressInfo;
